import {
  Button,
  Classes,
  Dialog,
  FormGroup,
  InputGroup,
  RadioGroup,
} from "@blueprintjs/core";
import { Businesses, Routes } from "@just-in-types";
import * as React from "react";
import { connect } from "react-redux";
import { emptyRoutes } from "../../adminFunctions/cleanSites";
import { firestore } from "../../firebase";
import { AddNewSite, addNewSite } from "../../store/actions";
import { convertToPath } from "../../views/Businesses";
import CopyLink from "../CopyLink";
import RouteInput from "./RouteInput";
import SignatureRouteInput from "./SignatureRouteInput";

interface ISiteAdderDispatchProps {
  onAddNewSite: AddNewSite;
}
interface ISiteAdderOwnProps {
  businessId: string;
  businessPath: string;
  onAddSite: (siteName: string) => void;
  onToggleShow: () => void;
  show: boolean;
}

type SiteAdderProps = ISiteAdderDispatchProps & ISiteAdderOwnProps;

interface ISiteAdderState {
  adding: boolean;
  allowOptOut: "yes" | "no";
  guid: string;
  kioskName: string;
  kioskNameEs: string;
  noCheckIn: "yes" | "no";
  path: string;
  routes: Routes.RoutesObject;
  showCoupons: "yes" | "no";
  siteName: string;
}

const isYesNo = (value: string): value is "yes" | "no" =>
  value === "yes" || value === "no";

interface InputProps {
  label: string;
  name: string;
  onChange: SiteAdder["handleInput"];
  value?: string;
}
const RadioInput = ({
  label,
  name,
  onChange,
  value,
}: InputProps & { value: "yes" | "no" }) => (
  <RadioGroup
    inline={true}
    label={label}
    name={name}
    onChange={onChange}
    options={[{ value: "yes" }, { value: "no" }]}
    selectedValue={value}
  />
);
const TextInput = ({
  id,
  label,
  name,
  onChange,
  value,
}: InputProps & { id: string; value: string }) => (
  <FormGroup label={label} labelFor={id}>
    <InputGroup {...{ id, name, onChange, value }} />
  </FormGroup>
);

export type HandleRouteInput = SiteAdder["handleRouteInput"];

class SiteAdder extends React.Component<SiteAdderProps, ISiteAdderState> {
  private initialState: ISiteAdderState = {
    adding: false,
    allowOptOut: "no",
    guid: "",
    kioskName: "",
    kioskNameEs: "",
    noCheckIn: "no",
    path: "",
    routes: emptyRoutes,
    showCoupons: "no",
    siteName: "",
  };

  constructor(props: SiteAdderProps) {
    super(props);

    this.state = this.initialState;
  }

  public render() {
    const { businessPath, onToggleShow, show } = this.props;
    const {
      adding,
      allowOptOut,
      kioskName,
      kioskNameEs,
      noCheckIn,
      path,
      routes: { dateofbirth, email, language, name, signature, vehicles, zip },
      showCoupons,
      siteName,
    } = this.state;

    return (
      <Dialog isOpen={show} onClose={onToggleShow} title="add new kiosk">
        <div className={Classes.DIALOG_BODY}>
          <TextInput
            id="site-name"
            label="kiosk location"
            onChange={this.handleInput}
            name="siteName"
            value={siteName}
          />
          <TextInput
            id="kiosk-name"
            label="kiosk name ( ivision )"
            onChange={this.handleInput}
            name="kioskName"
            value={kioskName}
          />
          <TextInput
            id="kiosk-name-es"
            label="spanish kiosk name ( ivision )"
            onChange={this.handleInput}
            name="kioskNameEs"
            value={kioskNameEs}
          />
          <RadioInput
            label="allow opt out"
            name="allowOptOut"
            onChange={this.handleInput}
            value={allowOptOut}
          />
          <RadioInput
            label="show coupons page"
            name="showCoupons"
            onChange={this.handleInput}
            value={showCoupons}
          />
          <RadioInput
            label="suppress check-in"
            name="noCheckIn"
            onChange={this.handleInput}
            value={noCheckIn}
          />
          <SignatureRouteInput
            label="signature"
            onChange={this.handleRouteInput}
            route={signature}
            routeName="signature"
          />
          <RouteInput
            label="date of birth"
            onChange={this.handleRouteInput}
            route={dateofbirth}
            routeName="dateofbirth"
          />
          <RouteInput
            label="email"
            onChange={this.handleRouteInput}
            route={email}
            routeName="email"
          />
          <RouteInput
            label="language"
            onChange={this.handleRouteInput}
            route={language}
            routeName="language"
          />
          <RouteInput
            label="name"
            onChange={this.handleRouteInput}
            route={name}
            routeName="name"
          />
          <RouteInput
            label="vehicles"
            onChange={this.handleRouteInput}
            route={vehicles}
            routeName="vehicles"
          />
          <RouteInput
            label="zip"
            onChange={this.handleRouteInput}
            route={zip}
            routeName="zip"
          />
          <CopyLink
            url={`https://kiosk.texnrewards.com/${businessPath}/${path}`}
          />
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button intent="success" loading={adding} onClick={this.addSite}>
              add kiosk
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }

  private handleInput = ({
    currentTarget: { name, value },
  }: React.FormEvent<HTMLInputElement>) => {
    if (name === "kioskName") {
      this.setState({ kioskName: value });
    }
    if (name === "kioskNameEs") {
      this.setState({ kioskNameEs: value });
    }
    if (name === "siteName") {
      this.setState({ path: convertToPath(value), siteName: value });
    }

    if (isYesNo(value)) {
      if (name === "allowOptOut") {
        this.setState({ allowOptOut: value });
      }
      if (name === "noCheckIn") {
        this.setState({ noCheckIn: value });
      }
      if (name === "showCoupons") {
        this.setState({ showCoupons: value });
      }
    }
  };

  private handleRouteInput = (
    routeName: Routes.RouteString,
    attribute: keyof Routes.Route | keyof Routes.SignatureRoute,
    value: boolean | string
  ) => {
    const { routes } = this.state;
    this.setState({
      routes: {
        ...routes,
        [routeName]: { ...routes[routeName], [attribute]: value },
      },
    });
  };

  private addSite = async () => {
    this.setState({ adding: true });

    const { businessId, onAddNewSite, onToggleShow } = this.props;
    const {
      allowOptOut,
      guid,
      kioskName,
      kioskNameEs,
      noCheckIn,
      path,
      routes,
      showCoupons,
      siteName,
    } = this.state;
    const convertToBoolean = (value: "yes" | "no") => value === "yes";
    const siteId = firestore.collection(`businesses/${businessId}/sites`).doc()
      .id;
    const site: Businesses.Site = {
      allowOptOut: convertToBoolean(allowOptOut),
      businessId,
      disableKeyboard: false,
      guid: guid || null,
      keyword: null,
      keywordEs: null,
      kioskName,
      kioskNameEs,
      noCheckIn: convertToBoolean(noCheckIn),
      path,
      routes,
      s3Id: null,
      shortcode:'',
      showCoupons: convertToBoolean(showCoupons),
      siteId,
      siteName,
    };

    await onAddNewSite({ businessId, site });

    this.setState(this.initialState);

    onToggleShow();
  };
}

const mapDispatchToProps = {
  onAddNewSite: addNewSite,
};

export default connect(
  null,
  mapDispatchToProps
)(SiteAdder);



// WEBPACK FOOTER //
// ./src/components/SiteAdder/index.tsx